import { Box, Button } from '@mui/material';
import { FormGroupSection } from 'module/documents/components/components';
import { WorkspaceUserAutocomplete } from 'module/documents/tabs/HumanResource/components';
import gamemasterLogo from 'assets/icons/gamemaster_Onboarding_WordIcon.png';
import { FC, useState } from 'react';
import { useDocumentContext } from 'module/documents/context';
import { Close } from '@mui/icons-material';
import { CreateEmployeePostPayload, submitCreateEmployeeForm } from 'core/API/documents';
import { useQueryClient } from 'react-query';
import { useWorkplacesContext } from 'module/workplaces/context/WorkplacesContext';
import { useWorkspaceResources } from 'core/hooks/broadcast';
import DoneIcon from '@mui/icons-material/Done';
import CircularProgress from '@mui/material/CircularProgress';

// Define the prop types
interface SelectedEmployeeTabProps {
	setdisableTabsForNewEmployee: React.Dispatch<React.SetStateAction<boolean>>;
  }

  const SelectedEmployeeTab: FC<SelectedEmployeeTabProps> = ({
	setdisableTabsForNewEmployee
  }) => {
    const { resetDocumentContext, handleModalClose, setTranslatorSignatureFile, setActiveTabIndex, newEmployee, selectedEmployee, setNewEmployeeCreated, formik, employeeState, setEmployeeState, setSelectedEmployee } = useDocumentContext();
    const { workplaceId } = useWorkplacesContext();
    const queryClient = useQueryClient();
    const { data: usersData, refetch: refetchWorkspaceUsers } = useWorkspaceResources();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSubmitted, setIsSubmitted] = useState(false);

    interface FormErrors {
      firstName: boolean;
      lastName: boolean;
      email: boolean;
      existingEmployee: boolean;
    }

    const [formErrors, setFormErrors] = useState<FormErrors>({
      firstName: false,
      lastName: false,
      email: false,
      existingEmployee: false,
    });

    const createEmployee = async (e) => {
      e.preventDefault();
      setIsSubmitting(true);
      setIsSubmitted(false);
      const errors = {
        firstName: false,
        lastName: false,
        email: false,
        existingEmployee: false,
      };
      let isValid = true;
    
      if (employeeState === "new_employee" || employeeState === "re_hiring_employee") {
        if (!newEmployee.firstName.trim()) {
          errors.firstName = true;
          isValid = false;
        }
        if (!newEmployee.lastName.trim()) {
          errors.lastName = true;
          isValid = false;
        }
        if (!newEmployee.email.trim()) {
          errors.email = true;
          isValid = false;
        } else {
          const emailPattern = /^[a-zA-Z0-9]+([._+-]?[a-zA-Z0-9]+)*@[a-zA-Z0-9]+([.-]?[a-zA-Z0-9]+)*\.[a-zA-Z]{2,}$/;
          if (!emailPattern.test(newEmployee.email.trim())) {
            errors.email = true;
            isValid = false;
          }
        }
      } else if (employeeState === "existing_employee") {
        if (!selectedEmployee) {
          errors.existingEmployee = true;
          isValid = false;
        }
      }
    
      setFormErrors(errors);
    
      if (isValid) {
        resetDocumentContext('dont_reset');
        if (employeeState === "new_employee" || employeeState === "re_hiring_employee") {
          if(employeeState === "re_hiring_employee") {formik.setFieldValue('employee_rehire', true);} else {formik.setFieldValue('employee_rehire', false);}
          const payload: CreateEmployeePostPayload = {
            first_name: newEmployee.firstName,
            middle_initial: newEmployee.middleName,
            last_name: newEmployee.lastName,
            email: newEmployee.email,
            active: true,
          };
  
          try {
            const response = await submitCreateEmployeeForm(payload, workplaceId); 
            const { data: updatedUsers } = await refetchWorkspaceUsers();
            const newUser = updatedUsers.find(
              (entry) => entry?.email === payload.email
            );
            if (newUser) {
              setSelectedEmployee(newUser);
              setEmployeeState("existing_employee");
            }
            console.log("NEW EMPLOYEE:", response, "PAYLOAD EMAIL", payload.email);
            // showToast('success', 'Form submitted successfully!');
            // Commented out - employee-form API calls not needed
            // queryClient.invalidateQueries([`employee-form/create-employee/${workplaceId}`]);
            setNewEmployeeCreated(true);
            setActiveTabIndex(1); // Set human resource tab active
            setdisableTabsForNewEmployee(false);
            setIsSubmitted(true);
            setTranslatorSignatureFile(null);
          } catch (error) {
            console.error('Error submitting form:', error);
            // showToast('error', 'Error submitting form!'); 
          }
        } else {
          setActiveTabIndex(1); //setting human resource tab active
          setdisableTabsForNewEmployee(false);
        }
      }
        // ✅ 4. STOP LOADING AFTER DELAY
      setTimeout(() => {
        setIsSubmitting(false);
      }, 800);
    };

    return (
    <>
      <Box display="flex" flexDirection="column" gap={2}>
        <Box 
          sx={{ 
            display: 'flex', 
            justifyContent: 'flex-start', 
            alignItems: 'center',
            marginBottom: '-20px', // Negative margin to pull the form closer
          marginTop: '-30px'
          }}
        >
          <img 
            src={gamemasterLogo} 
            alt="GameMaster Onboarding" 
            style={{ height: '90px', width: 'auto', margin: '0' }} // Reduced height
          />
        </Box>
        <FormGroupSection>
        <WorkspaceUserAutocomplete 
              setdisableTabsForNewEmployee={setdisableTabsForNewEmployee}
          setActiveTabIndex={setActiveTabIndex}
          formErrors={formErrors}
            />
        </FormGroupSection>
      </Box>
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            gap: 2,
            marginTop: 2,
          }}
        >
          <Button
            variant="outlined"
            onClick={handleModalClose}
            sx={{
              backgroundColor: "#E0E0E0",
              color: "#333",
              padding: "12px 16px",
              borderRadius: "8px",
              fontSize: "16px",
              fontWeight: "bold",
              display: "flex",
              alignItems: "center",
              gap: "8px",
              border: "1px solid #BDBDBD",
              transition: "all 0.3s ease",
              "&:hover": {
                backgroundColor: "#D6D6D6",
                transform: "translateY(-2px)",
              },
            }}
          >
            <Close /> Cancel
          </Button>
            <Button
              variant="contained"
              onClick={createEmployee}
              disabled={isSubmitting} // ✅ DISABLE WHILE SUBMITTING
              startIcon={
                isSubmitting ? ( // ✅ ICON WHILE LOADING
                  <CircularProgress color="inherit" size={24} />
                ) : isSubmitted ? ( // ✅ SHOW TICK AFTER SUBMIT
                  <DoneIcon />
                ) : null
              }
              sx={{
                backgroundColor: "#1976D2",
                color: "white",
                padding: "12px 24px",
                borderRadius: "8px",
                fontSize: "16px",
                fontWeight: "bold",
                display: "flex",
                alignItems: "center",
                gap: "8px",
                boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.1)",
                transition: "all 0.3s ease",
                "&:hover": {
                  backgroundColor: "#115293",
                  transform: "translateY(-2px)",
                },
              }}
            >
              Next <span style={{ fontSize: "18px" }}>➜</span>
            </Button>
        </Box>
    </>
	);
  };

export default SelectedEmployeeTab;
